// src/MathTools/LimitExplorerTool.js
//-------------------------------------------------------------
import React, { useState, useMemo } from "react";

/* safe builder */
const buildFn = expr => {
  try { 
    // eslint-disable-next-line no-new-func
    return new Function("x", `with(Math){ return ${expr}; }`); }
  catch { return () => NaN; }
};

/* default: removable discontinuity (x²–1)/(x–1) limit = 2 at a = 1 */
export default function LimitExplorerTool({
  expr = "(x**2 - 1)/(x - 1)",
  a    = 1,
  xMin = -1,          // graph window
  xMax = 3,
  yMin = 0,
  yMax = 4
}) {
  const f = useMemo(()=>buildFn(expr), [expr]);
  const [delta, setDelta] = useState(1);

  /* three x-values */
  const xL = a - delta,  xR = a + delta;

  const pts = [
    { label:`x=${xL.toFixed(3)}`,  x:xL, y:f(xL), color:"#E15554" },
    { label:`x→${a}`,             x:a,  y:f(a),  color:"#3BB273" },
    { label:`x=${xR.toFixed(3)}`,  x:xR, y:f(xR), color:"#4D9DE0" }
  ];

  /* simple scales */
  const W = 320, H = 160,
        px = x=>((x - xMin)/(xMax - xMin))*W,
        py = y=>H - ((y - yMin)/(yMax - yMin))*H;

  /* sample curve (100 pts) */
  const curve = Array.from({length:101},(_,i)=>{
    const x = xMin + i*(xMax-xMin)/100;
    const y = f(x);
    return isFinite(y) ? `${px(x)},${py(y)}` : null;
  }).filter(Boolean).join(" ");

  return (
    <div style={{ border:"1px solid #ccc", padding:10, fontSize:13, maxWidth:360 }}>
      <strong>Limit Explorer</strong>
      <span style={{ fontSize:12 }}> &nbsp;f(x) = {expr}</span>

      {/* slider */}
      <div>
        Δ&nbsp;
        <input type="range" min={0.01} max={Math.min(2,a-xMin,xMax-a)} step={0.01}
               value={delta} onChange={e=>setDelta(+e.target.value)}
               style={{ width:160, verticalAlign:"middle" }}/>
        &nbsp;{delta.toFixed(2)}
      </div>

      {/* SVG graph */}
      <svg width={W} height={H} style={{ background:"#fafafa", marginTop:6 }}>
        {/* axes */}
        <line x1={px(xMin)} y1={py(0)} x2={px(xMax)} y2={py(0)} stroke="#888"/>
        <line x1={px(0)} y1={py(yMin)} x2={px(0)} y2={py(yMax)} stroke="#888"/>

        {/* function curve */}
        <polyline points={curve} fill="none" stroke="#999" strokeWidth="1.5"/>

        {/* sample dots */}
        {pts.map((p,i)=> isFinite(p.y) && (
          <circle key={i} cx={px(p.x)} cy={py(p.y)} r={4} fill={p.color}/>
        ))}
      </svg>

      {/* numeric read-out */}
      <ul style={{ paddingLeft:18, margin:60 }}>
        {pts.map((p,i)=>
          <li key={i} style={{ color:p.color }}>
            {p.label}: {isFinite(p.y)?p.y.toFixed(4):"undefined"}
          </li>)}
      </ul>

      <p style={{ fontStyle:"italic", margin:0 }}>
        Shrink Δ → left/right points close in on <em>a</em>; f(x) approaches the
        limit (green).
      </p>
    </div>
  );
}
